import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import type { Observable } from 'rxjs';
import { Subject, map } from 'rxjs';
import type { ClinicInterface } from '../models/clinic.interface';
import { GetReportOwnerHeaderQuery, GetReportOwnerHeaderQueryVariables } from './report.service';
import type { ClinicWithOrthancModalityInterface } from '../../components/clinics/models/admin-clinic.interface';

@Injectable({
  providedIn: 'root'
})
export class ClinicService {
  private readonly selectedClinicChangedSource = new Subject<ClinicInterface>();
  selectedClinicChanged$ = this.selectedClinicChangedSource.asObservable();

  private readonly clinicAddedSource = new Subject<ClinicWithOrthancModalityInterface>();
  clinicAdded$ = this.clinicAddedSource.asObservable();

  GetReportHeadersQuery = gql`query getClinicReportHeader($clinicId: Long!) {
    clinicById(clinicId : $clinicId) {
      id
      ownerReportHeaderTemplate
      clinicReportHeaderTemplate
    }
  }`;

  constructor(private readonly apollo: Apollo) { }

  selectClinic(clinic: ClinicInterface): void {
    this.selectedClinicChangedSource.next(clinic);
  }

  /**
   * Émet un événement d'ajout de clinique
   * @param clinic La clinique ajoutée
   */
  emitClinicAdded(clinic: ClinicWithOrthancModalityInterface): void {
    this.clinicAddedSource.next(clinic);
  }

  getReportHeaders(clinicId: number): Observable<ClinicInterface> {
    return this.apollo.query<{ clinicById: ClinicInterface }>({
      query: this.GetReportHeadersQuery,
      variables: { clinicId }
    }).pipe(map(res => res.data.clinicById));
  }

  updateReportHeaders(clinicId: number, ownerHeaderTemplate: string, clinicHeaderTemplate: string): Observable<ClinicInterface | undefined> {
    return this.apollo.mutate<{
      updateClinicReportHeaders: ClinicInterface;
    }>({
      mutation: gql`mutation updateClinicReportHeaders($dto : ClinicReportHeadersDtoInput)
          {
            updateClinicReportHeaders( clinicReportHeadersDto : $dto)
              {
                id
                ownerReportHeaderTemplate
                clinicReportHeaderTemplate
              }
          }`,
      variables:
      {
        dto:
        {
          clinicId,
          ownerHeaderTemplate,
          clinicHeaderTemplate
        }
      },
      refetchQueries: [
        {
          query: this.GetReportHeadersQuery,
          variables: { clinicId }
        },
        {
          query: GetReportOwnerHeaderQuery,
          variables: GetReportOwnerHeaderQueryVariables
        }
      ]
    }

    ).pipe(map(res => res.data ? res.data?.updateClinicReportHeaders : undefined));
  }


  userClinics(userId: number, onlyMyPacs: boolean): Observable<ClinicInterface[]> {
    return this.apollo.query<{ userClinics: ClinicInterface[] }>({
      query: gql`query userClinics($userId : Long!, $where: ClinicFilterInput) {
        userClinics(userId : $userId, where: $where) {
          id
          name
          city
          phone
          shortName
          logoUrl
          isGroup
          isMyPacs
          hasMyPacsAccess
          isLite
          hasSisters
          hasPaymentMethod
          parent {
            id
            name
          }
        }
      }`,
      variables: {
        userId,
        // Inclure les cliniques MyPacs natives ET les cliniques SCC avec accès MyPacs activé
        where: onlyMyPacs ? {
          or: [
            { isMyPacs: { eq: true } },
            { hasMyPacsAccess: { eq: true } }
          ]
        } : undefined
      }
    }).pipe(map(res => res.data.userClinics));
  }

  searchUserClinics(searchText: string): Observable<ClinicInterface[]> {
    return this.apollo.query<SearchClinicsReponse>({
      query: gql`query searchUserClinics($searchText: String!) {
        userClinics(order: { name: ASC }, where: {
          and: [

            { or : [
              { name : { contains : $searchText } },
              { alias : {contains : $searchText } }
            ] }
          ]
        }) {
          id
          name
          shortName
          alias
          isGroup
          hasPaymentMethod
        }
      }`,
      variables: { searchText }
    }).pipe(map(res => res.data.userClinics));
  }

  // NOTE: Requesting a very high take value (10000) to effectively remove pagination limit
  // See admin-clinic.service.ts GetAllClinicsWithModalityQuery for explanation
  searchAllClinics(searchText: string): Observable<ClinicInterface[]> {
    return this.apollo.query<SearchAllClinicsReponse>({
      query: gql`query searchAllClinics($searchText: String!, $isGroup: Boolean!) {
        clinics(order: { name: ASC }, where : {
          and : [
            { isGroup : { eq : $isGroup} },
            { or : [
              { name : {contains : $searchText } },
              { alias : {contains : $searchText } }
            ] }
          ]
        }, take: 10000) {
          items {
            id
            name
            shortName
            alias
            isGroup
          }
        }
      }`,
      variables: { searchText, isGroup: false }
    }).pipe(map(res => res.data.clinics?.items ?? []));
  }

  searchAllClinicsByZipcode(searchText: string, zipCode: string): Observable<ClinicInterface[]> {
    return this.apollo.query<{ searchClinics: { object: ClinicInterface }[] }>({
      query: gql`query searchAllClinicsByZipcode($searchText: String!, $zipCode: String! ) {
        searchClinics(search: $searchText, zipCode: $zipCode, take: 10) {
          object {
            id
            name
            alias
            city
            phone
            hasPaymentMethod
            isMyPacs
          }
        }
      }`,
      variables: { searchText, zipCode }
    }).pipe(map(res => res.data.searchClinics.map(s => s.object)));
  }
}


export interface SearchClinicsReponse {
  userClinics: ClinicInterface[];
}

export interface SearchAllClinicsReponse {
  clinics: { items: ClinicInterface[] };
}
